import type {
  AnchorHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
} from "react";
import Image from "next/image";
import Link from "next/link";
import type { MDXComponents } from "mdx/types";
import styles from "./seo-page.module.css";

type CalloutProps = {
  children: ReactNode;
  title: string;
  variant?: "note" | "tip" | "warning";
};

type FigureProps = {
  alt: string;
  caption?: string;
  src: string;
};

export function Callout({
  children,
  title,
  variant = "note",
}: CalloutProps) {
  return (
    <aside className={styles.callout} data-variant={variant}>
      <strong>{title}</strong>
      <div>{children}</div>
    </aside>
  );
}

export function Screenshot(props: FigureProps) {
  return <MediaFigure {...props} kind="screenshot" />;
}

export function Diagram(props: FigureProps) {
  return <MediaFigure {...props} kind="diagram" />;
}

function MediaFigure({
  alt,
  caption,
  kind,
  src,
}: FigureProps & { kind: "diagram" | "screenshot" }) {
  return (
    <figure className={styles.figure} data-kind={kind}>
      <div className={styles.figureImage}>
        <Image
          fill
          alt={alt}
          sizes="(max-width: 800px) 100vw, 1120px"
          src={src}
          unoptimized={src.endsWith(".svg")}
        />
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function ComparisonTable(props: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div
      className={styles.tableRegion}
      role="region"
      aria-label="Comparison table"
      tabIndex={0}
    >
      <table {...props} />
    </div>
  );
}

function SeoLink({ href = "", ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (href.startsWith("/")) {
    return <Link href={href} {...props} />;
  }

  return <a href={href} rel={href.startsWith("https://") ? "noopener" : undefined} {...props} />;
}

function HeadingTwo(props: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 {...props} />;
}

function HeadingThree(props: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 {...props} />;
}

export const seoMdxComponents = {
  Callout,
  Diagram,
  Screenshot,
  a: SeoLink,
  h2: HeadingTwo,
  h3: HeadingThree,
  table: ComparisonTable,
} satisfies MDXComponents;
